
import window from 'bits/global'
import { Component, Getter, namespace, Vue, Watch } from 'bits/vue'
import dragAndDropHexagon from 'images/drag_and_drop_hexagon.svg'
import dragAndDropHexagonOutline from 'images/drag_and_drop_hexagon_outline.svg'
import type { DraftPost, PostAttributes, PostModalDropZone } from 'types'
import { ComposerModalAlertData } from 'vuexstore/modules/composer_modal_alert'
import { DraggedDataType } from 'vuexstore/modules/drag_and_drop_events'

const composerModal = namespace('composerModal')
const composerModalAlert = namespace('composerModalAlert')
const contentPicker = namespace('contentPicker')
const dragAndDropEvents = namespace('dragAndDropEvents')
const draft = namespace('draft')
const post = namespace('post')
const mapModule = namespace('map')

@Component({})
export default class SurfaceDragAndDropOverlay extends Vue {
  $refs!: {
    draggingOverlay: HTMLDivElement
  }

  dragAndDropHexagon = dragAndDropHexagon
  dragAndDropHexagonOutline = dragAndDropHexagonOutline

  @Getter isAPostDragging!: boolean
  @composerModal.Getter('isDocked') composerModalShouldDock!: boolean
  @contentPicker.Getter('xContentSourceMenu') isContentPickerOpened!: boolean
  @dragAndDropEvents.Getter isHandlingDragEvent!: boolean
  @dragAndDropEvents.Getter dropActionMessage!: string
  @dragAndDropEvents.Getter draggedDataType!: DraggedDataType
  @dragAndDropEvents.Getter postModalDropZone!: PostModalDropZone
  @draft.Getter activeDraft!: DraftPost
  @draft.Getter activeDraftUploadingFile!: File | null | undefined
  @draft.Getter isAnyDraftActive!: boolean
  @mapModule.Getter('isDraggingLocationPickerPin') isDraggingLocationPickerPinInMap!: boolean
  @mapModule.Getter('isDraggingPosts') isDraggingPostsInMap!: boolean
  @composerModal.Action('dock') dockComposerModal!: () => void
  @composerModalAlert.Action openAlert!: (data: ComposerModalAlertData) => void
  @composerModalAlert.Action resetAlert!: () => void
  @contentPicker.Action hideContentSourceMenu!: () => void
  @draft.Action uploadFileActiveDraft!: (payload: { file: File }) => void
  @draft.Action updateActiveDraft!: (post: PostAttributes) => void
  @draft.Action startNewDraft!: (payload: { attributes: PostAttributes; shouldStartEditing: boolean }) => void
  @dragAndDropEvents.Action startHandlingDragEvent!: () => void
  @dragAndDropEvents.Action stopHandlingDragEvent!: () => void
  @dragAndDropEvents.Action setDraggedDataType!: (payload: { newType: DraggedDataType }) => void
  @dragAndDropEvents.Action setDropActionMessage!: (payload: { message: string }) => void
  @post.Action createPost!: (payload: { attributes: PostAttributes }) => void

  get backgroundClasses(): string[] {
    if (!this.isAnyDraftActive) {
      return ['bg-grape-500', 'dark:bg-drag-overlay-dark', 'bg-opacity-50', 'dark:bg-opacity-35']
    } else {
      // transparent and change the color of the post modal's overlay instead
      return ['bg-transparent']
    }
  }

  get xMessageHexagon(): boolean {
    return !this.isAnyDraftActive
  }

  get isAnyComposerModalDocked(): boolean {
    return this.isAnyDraftActive && this.composerModalShouldDock
  }

  get isMapHandlingDragEvent(): boolean {
    return this.isDraggingLocationPickerPinInMap || this.isDraggingPostsInMap
  }

  initFileDragEvent(): void {
    this.setDraggedDataType({ newType: DraggedDataType.File })
    let message = ''
    if (this.isAnyDraftActive) {
      message = this.isAnyComposerModalDocked
        ? this.__('Drop the file here to add it to this post')
        : this.__('Drop the file anywhere to add it to this post')
    } else {
      message = this.__('Drop files anywhere to create a new post')
    }
    this.setDropActionMessage({ message })
  }

  initLinkDragEvent(): void {
    this.setDraggedDataType({ newType: DraggedDataType.Link })

    let message = ''
    if (this.isAnyDraftActive) {
      message = this.isAnyComposerModalDocked
        ? this.__('Drop the link here to add it to this post')
        : this.__('Drop the link anywhere to add to this post')
    } else {
      message = this.__('Drop links anywhere to create a new post')
    }

    this.setDropActionMessage({ message })
  }

  initTextDragEvent(): void {
    this.setDraggedDataType({ newType: DraggedDataType.Text })

    let message = ''
    if (this.isAnyDraftActive) {
      message = this.isAnyComposerModalDocked
        ? this.__('Drop the text here to add it to this post')
        : this.__('Drop the text anywhere to add it to this post')
    } else {
      message = this.__('Drop text anywhere to create a new post')
    }

    this.setDropActionMessage({ message })
  }

  setupDragEvent(event: DragEvent): void {
    if (!event.dataTransfer) {
      this.stopHandlingDragEvent()
      return
    }

    const dataTransferTypes = event.dataTransfer.types

    this.resetAlert()

    if (dataTransferTypes.includes('Files')) {
      // File(s) are being dragged
      this.initFileDragEvent()
    } else if (dataTransferTypes.includes('text/uri-list')) {
      // Link(s) are being dragged
      this.initLinkDragEvent()
    } else if (dataTransferTypes.includes('text/plain')) {
      // Text is being dragged
      this.initTextDragEvent()
    } else {
      this.stopHandlingDragEvent()
    }
  }

  handleFileDrop({
    files,
    left,
    top,
    shouldAddToActiveDraft,
  }: {
    files: FileList
    left: number
    top: number
    shouldAddToActiveDraft: boolean
  }): void {
    if (!files || files.length === 0) {
      return
    }

    if (shouldAddToActiveDraft) {
      if (files.length > 1) {
        this.openAlert({
          title: this.__('Multiple files added'),
          body: this.__('While writing a post only one file can be added at a time'),
          primaryActionText: this.__('Okay'),
          primaryActions: [],
        })
        return
      }

      const fileToUpload = files[0]

      // If there is already an attachment in the active draft or an attachment being uploaded to the active draft
      if (this.activeDraft.attachment || this.activeDraftUploadingFile) {
        const primaryActions = this.activeDraft.attachment
          ? [
              // an attachment already exists
              { type: 'draft/removeDraftAttachment', payload: { postCid: this.activeDraft.cid } },
              { type: 'draft/uploadFileActiveDraft', payload: { file: fileToUpload } },
            ]
          : [
              // an attachment is being uploaded
              // Note: when the user click "Replace", it can either be still uploading or already uploaded and added to activeDraft.attachment
              { type: 'draft/replaceActiveDraftUploadingFile', payload: { file: fileToUpload } },
            ]
        this.openAlert({
          title: this.__('An attachment already exists'),
          body: this.__('Are you sure you want to replace your current attachment?'),
          primaryActionText: this.__('Replace'),
          secondaryActionText: this.__('Cancel'),
          secondaryActions: [],
          primaryActions,
        })
        return
      }

      this.uploadFileActiveDraft({ file: fileToUpload })
    } else {
      // create new posts

      if (files.length === 1) {
        // if there is only 1 file, we may open a docked draft and upload it inside
        const file = files[0]
        const attributes = {
          file,
          left,
          top,
        }
        const shouldStartEditing = !this.isAnyDraftActive
        this.startNewDraft({ attributes, shouldStartEditing })
        if (shouldStartEditing) {
          this.dockComposerModal()
        }
      } else {
        // upload the files in mininmized drafts
        // Copied from drop_to_post_mixin.js
        // cannot use foreach because files is a FileList and not an array
        for (let idx = 0; idx < files.length; ++idx) {
          const file = files[idx]
          const newPost = {
            attributes: {
              file,
              left: left + idx * 8,
              top: top + idx * 8,
            },
          }
          this.createPost(newPost)
        }
      }
    }
  }

  handleLinkDrop({
    link,
    left,
    top,
    shouldAddToActiveDraft,
  }: {
    link: string
    left: number
    top: number
    shouldAddToActiveDraft: boolean
  }): void {
    if (!link) {
      return
    }

    if (shouldAddToActiveDraft) {
      // if a post modal is opened, try adding it to the post

      // If there is already an attachment in the active draft or an attachment being uploaded to the active draft
      if (this.activeDraft.attachment || this.activeDraftUploadingFile) {
        this.openAlert({
          title: this.__('An attachment already exists'),
          body: this.__('Are you sure you want to replace your current attachment?'),
          primaryActionText: this.__('Replace'),
          secondaryActionText: this.__('Cancel'),
          secondaryActions: [],
          primaryActions: [{ type: 'draft/replaceActiveDraftAttachmentWithLink', payload: { link } }],
        })
        return
      }

      this.updateActiveDraft({
        attachment: link,
        wish_content: {
          attachment_props: {
            url: link,
          },
          is_processed: false,
        },
      })
    } else {
      // create a new post

      const attributes = {
        attachment: link,
        left,
        top,
      }

      const shouldStartEditing = !this.isAnyDraftActive
      this.startNewDraft({ attributes, shouldStartEditing })
      if (shouldStartEditing) {
        this.dockComposerModal()
      }
    }
  }

  handleTextDrop({
    text,
    left,
    top,
    shouldAddToActiveDraft,
  }: {
    text: string
    left: number
    top: number
    shouldAddToActiveDraft: boolean
  }): void {
    if (!text) {
      return
    }

    // Need to replace '\n' with '<br/>' for trix editor
    const trixFriendlyText = text.replace(/\n/g, '<br/>')

    if (shouldAddToActiveDraft) {
      // if a post modal is opened, try adding it to the post

      this.updateActiveDraft({
        body: (this.activeDraft.body || '') + '<br/>' + trixFriendlyText,
      })
    } else {
      // create a new post
      const attributes = {
        body: trixFriendlyText,
        left,
        top,
      }

      const shouldStartEditing = !this.isAnyDraftActive
      this.startNewDraft({ attributes, shouldStartEditing })
      if (shouldStartEditing) {
        this.dockComposerModal()
      }
    }
  }

  onDrop(event: DragEvent): void {
    if (this.isContentPickerOpened) {
      this.hideContentSourceMenu()
    }

    this.stopHandlingDragEvent()

    const dataTransfer = event.dataTransfer
    if (!dataTransfer) {
      return
    }

    // Initialize top and left position for new wishes
    // Copied from drop_to_post_mixin.js
    const wishListElement = document.getElementById('wish-list')
    const left = Math.max(event.pageX - (wishListElement ? wishListElement.getBoundingClientRect().left : 0), 0)
    const top = Math.max(event.pageY - (wishListElement ? wishListElement.getBoundingClientRect().top : 0), 0)

    const droppedOutsideActiveDockedComposerModal =
      this.isAnyComposerModalDocked &&
      this.postModalDropZone &&
      (event.clientX < this.postModalDropZone.left ||
        event.clientX > this.postModalDropZone.right ||
        event.clientY < this.postModalDropZone.top ||
        event.clientY > this.postModalDropZone.bottom)

    const shouldAddToActiveDraft = this.isAnyDraftActive && !droppedOutsideActiveDockedComposerModal

    if (this.draggedDataType === DraggedDataType.File) {
      this.handleFileDrop({ files: dataTransfer.files, left, top, shouldAddToActiveDraft })
    } else if (this.draggedDataType === DraggedDataType.Link) {
      this.handleLinkDrop({ link: dataTransfer.getData('text/uri-list'), left, top, shouldAddToActiveDraft })
    } else if (this.draggedDataType === DraggedDataType.Text) {
      this.handleTextDrop({ text: dataTransfer.getData('text/plain'), left, top, shouldAddToActiveDraft })
    }
  }

  checkAndStartHandlingDragEvent(event: DragEvent): void {
    // only start handling dragevent if there is data dragged and it's not the location pin in map format/an element on the page

    // when an html element is dragged it can be text/html and any of text/uri-list, Files
    // when text is dragged, it's text/html, text/plain
    // => the condition event.dataTransfer.types.includes('text/html') && !event.dataTransfer.types.includes('text/plain') is to catch html element but not plain text
    const isDraggingElementOnPage =
      !!event?.dataTransfer?.types &&
      event.dataTransfer.types.includes('text/html') &&
      !event.dataTransfer.types.includes('text/plain')
    if (!!event.dataTransfer && !this.isMapHandlingDragEvent && !isDraggingElementOnPage && !this.isAPostDragging) {
      this.startHandlingDragEvent()
    }
  }

  // if the overlay is shown but there is no message, it's likely a bug. Hide the overlay for now so it won't block the whole page
  @Watch('isHandlingDragEvent')
  onOverlayShown(isHandlingDragEvent: boolean): void {
    if (isHandlingDragEvent) {
      setTimeout(() => {
        if (!this.dropActionMessage) {
          this.stopHandlingDragEvent()
        }
      }, 500)
    }
  }

  mounted(): void {
    window.addEventListener('dragenter', this.checkAndStartHandlingDragEvent)
  }

  beforeDestroy(): void {
    window.removeEventListener('dragenter', this.checkAndStartHandlingDragEvent)
  }
}
